import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Card from '@material-ui/core/Card';
import authService from './api-authorization/AuthorizeService';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import '../custom.css';

import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import * as pbi from 'powerbi-client';
import {
    MenuItem,
    Select,
    FormControl,
    IconButton,
    LinearProgress
} from '@material-ui/core';
import GetAppIcon from '@material-ui/icons/GetApp';

const useStyles = makeStyles({
    root: {
        minWidth: 275
    }
});

interface ReportData {
    reportId: string;
    reportName: string;
    groupId: string;
}

export default function Report() {
    const classes = useStyles();
    const [reportsList, setReportList] = useState<ReportData[]>([]);
    const [selectedIndex, setIndexValue] = useState<number>(0);
    const [reportId, setReportId] = useState<string>('');
    const [datasetId, setDatasetId] = useState<string>('');
    const [embedUrl, setEmbedUrl] = useState<string>('');
    const [accessToken, setAccessToken] = useState<string>('');
    const [colorThemeRGB, setColorThemeRGB] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [reportName, setReportName] = useState<string>('');
    const [isDownloading, setIsDownloading] = useState<boolean>(false);
    const [report, setReport] = useState<pbi.Report>();

    useEffect(() => {
        getAppInfo();
        populateReportsData();
    }, []);

    const getAppInfo = async () => {
        setIsLoading(true);
        const token = await authService.getAccessToken();
        const response = await fetch('api/app/info', {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.ok) {
            const data = await response.json();
            setColorThemeRGB(data.colorThemeRGB);
        }
        setIsLoading(false);
    };

    const downloadReport = async (reportId: string, reportName: string, currentPage: boolean) => {
        setIsDownloading(true);
        const token = await authService.getAccessToken();
        let reportPageParam = '';

        if (currentPage) {
            if (!report) {
                setIsDownloading(false);
                return;
            }
            const activePage = await getActivePage(report);
            if (!activePage) {
                setIsDownloading(false);
                return;
            }

            reportPageParam = '&pageName=' + activePage.name;
        }

        const response = await fetch(
            `api/bireport/DownloadReport?reportId=${reportId}&datasetId=${datasetId}` + reportPageParam,
            { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        );

        if (response.ok) {
            const data = await response.blob();
            const filename = `${reportName}.pdf`;
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
        setIsDownloading(false);
    };

    const populateReportsData = async () => {
        setIsLoading(true);
        const user = await authService.getUser();
        const userId = user ? user.sub : null;
        const token = await authService.getAccessToken();
        const response = await fetch(`api/users/GetUserBIReports/${userId}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
        });

        if (response.ok) {
            const data = await response.json();
            setReportList(data);
            if (data.length > 0) {
                await populateReportData(data[selectedIndex].groupId, data[selectedIndex].reportId);
            }
        }
        setIsLoading(false);
    };

    const handleReportChange = async (event: React.ChangeEvent<{ value: unknown }>) => {
        setIsLoading(true);
        const selectedReportId = event.target.value as string;
        const report = reportsList.find(report => report.reportId === selectedReportId);
        if (report) {
            setIndexValue(reportsList.indexOf(report));
            await populateReportData(report.groupId, report.reportId);
        }
        setIsLoading(false);
    };

    // new function to get active page from report
    const getActivePage = async (report: pbi.Report) => {
        const pages = await report.getPages();
        const activePage = pages.find(page => page.isActive);
        return activePage;
    };
    
    const populateReportData = async (groupId: string, reportId: string) => {
        setIsLoading(true);
        const token = await authService.getAccessToken();
        const user = await authService.getUser();
        const userId = user ? user.sub : null;

        const response = await fetch('api/bireport/GetReportDetails', {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ Id: reportId, GroupId: groupId, UserId: userId })
        });

        if (response.ok) {
            const data = await response.json();
            setReportId(data.reportId);
            setDatasetId(data.datasetId);
            setReportName(data.reportName);
            setEmbedUrl(data.embedUrl);
            setAccessToken(data.token);
        }
        setIsLoading(false);
    };

    return (
        <div className="dashboard-inner">
            <Card>
                <CardContent>
                    <FormControl style={{ maxWidth: 400 }}>
                        <Typography variant="h6" style={{ color: colorThemeRGB }}>
                            Select Report
                        </Typography>
                        <Select label={'Select Report:'} value={reportId} onChange={handleReportChange}>
                            {reportsList.length > 0 ? (
                                reportsList.map(report => (
                                    <MenuItem key={report.reportId} value={report.reportId}>
                                        {report.reportName}
                                    </MenuItem>
                                ))
                            ) : (
                                <MenuItem value="0">No Reports Available</MenuItem>
                            )}
                        </Select>
                    </FormControl>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <IconButton
                            disabled={isDownloading || !reportId || isLoading}
                            onClick={() => downloadReport(reportId, reportName, false)}
                        >
                            <GetAppIcon />
                            Export Full Report
                        </IconButton>
                        <IconButton
                            disabled={isDownloading || !reportId || isLoading}
                            onClick={() => downloadReport(reportId, reportName, true)}
                        >
                            <GetAppIcon />
                            Export Current Page
                        </IconButton>
                    </div>
                    {isDownloading && <LinearProgress />}
                </CardContent>
                <CardContent style={{ height: 'calc(100vh - 150px)' }}>
                    <PowerBIEmbed
                        cssClassName='report-container'
                        embedConfig={{ type: 'report', id: reportId, embedUrl, accessToken, tokenType: models.TokenType.Embed }}
                        eventHandlers={new Map([
                            ['loaded', () => console.log('Report loaded')]
                        ])}
                        getEmbeddedComponent = { (embedObject: pbi.Embed) => {
                            setReport(embedObject as pbi.Report);
                        } }
                    />
                </CardContent>
            </Card>
        </div>
    );
}
